import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Star, ShieldCheck, Briefcase, ExternalLink, Award, Clock, Phone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import PaymentHandleList from "@/components/profile/PaymentHandleList";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import NotFound from "@/pages/NotFound";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import api, { mediaUrl } from "@/lib/api";
import { useParams } from "react-router-dom";
import { formatDate } from "@/lib/dates";

export default function ProviderLanding() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const cta = useSmartCTA();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  // A slug with no FixyBee behind it answers 404, and the catch below used to
  // swallow that into an endless skeleton ([CUENTAS-ADMIN]: every showcase
  // link that outlived its account looked like a page still loading).
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setMissing(false);
    api.get(`/users/providers/${slug}`).then((r) => {
      setProvider(r.data);
      return api.get(`/reviews?provider_id=${r.data.user_id}`);
    }).then((r) => setReviews(r?.data?.reviews || [])).catch((err) => {
      if (err?.response?.status === 404) setMissing(true);
    });
  }, [slug]);

  if (missing) return <NotFound />;

  if (!provider) return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Navbar /><div className="section container-app skeleton h-64" /><Footer />
    </div>
  );

  // One page for every FixyBee: no Master hero, no plan badge (PLANES M5 —
  // the plan is never shown to a client). The payload no longer carries it.
  // Every plan has the slogan and the portfolio since PLANES (2026-09-30).
  const showSlogan = !!provider.landing_slogan;

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="provider-landing">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="section bg-honeycomb">
          <div className="container-app grid md:grid-cols-[1fr_2fr] gap-10 items-center">
            <div className="flex flex-col items-center">
              {provider.business_logo_url ? (
                <div className="w-40 h-40 rounded-md bg-cream flex items-center justify-center shadow-deep overflow-hidden p-2" data-testid="provider-logo">
                  <img
                    src={mediaUrl(provider.business_logo_url)}
                    alt={`${provider.company_name || provider.full_name} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              ) : provider.avatar_url ? (
                <img
                  src={mediaUrl(provider.avatar_url)}
                  alt={provider.full_name}
                  className="w-40 h-40 rounded-full bg-cream object-cover shadow-deep"
                  data-testid="provider-avatar"
                />
              ) : (
                <div className="w-40 h-40 rounded-full bg-cream flex items-center justify-center font-display font-extrabold text-worker text-7xl shadow-deep">
                  {(provider.full_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              {/* Two marks, two facts (2026-10-28): identity checked (gold,
                  shield) and phone confirmed (neutral, phone). Neither orders
                  anything — `TRUST_ORDER` reads only the first. */}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {provider.is_verified && (
                  <span className="badge badge-verified" data-testid="verified-badge"><ShieldCheck size={12} /> {t("providerLanding.verified")}</span>
                )}
                {provider.phone_verified && (
                  <span className="badge" data-testid="phone-verified-badge"><Phone size={12} /> {t("signupX.publicPhoneVerified")}</span>
                )}
              </div>
            </div>
            <div>
              {showSlogan && (
                <p className="font-display italic text-xl text-worker/80" data-testid="provider-slogan">"{provider.landing_slogan}"</p>
              )}
              <h1 className="font-display text-5xl md:text-6xl font-extrabold leading-tight text-worker">
                {provider.company_name || provider.full_name}
              </h1>
              <p className="mt-3 text-lg text-smoke">
                <MapPin size={14} className="inline mr-1" />
                {provider.location_city}{provider.location_state ? `, ${provider.location_state}` : ""}
              </p>
              <p className="mt-4 text-lg leading-relaxed max-w-2xl text-worker">
                {provider.landing_bio || provider.bio || provider.services_summary}
              </p>
              <div className="mt-6 flex gap-3 flex-wrap">
                {/* The button carries who it came from ([FIX] 2026-09-07).
                    It used to be a bare link to the job form, so a request
                    aimed at this FixyBee by name went out to every FixyBee in
                    the category instead. The ternary it replaces was dead:
                    `postJob()` already returns /client/jobs/new for a client. */}
                <Link
                  to={cta.postJob({
                    // Not when a FixyBee is looking at their own page: the
                    // server refuses `cannot_direct_to_self`, and finding that
                    // out at the end of a filled-in form is finding out late.
                    forProvider: cta.user?.user_id === provider.user_id
                      ? null
                      : (provider.public_slug || slug),
                  })}
                  className="fx-btn fx-btn--primary"
                  data-testid="provider-landing-invite"
                >
                  {t("providerLanding.invite")}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="section bg-cream">
          <div className="container-app grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: t("providerLanding.statsRating"), value: provider.average_rating || "—", icon: Star },
              { label: t("providerLanding.statsQuality"), value: provider.avg_quality || "—", icon: Award },
              { label: t("providerLanding.statsResp"), value: provider.avg_responsibility || "—", icon: Clock },
              { label: t("providerLanding.statsJobs"), value: provider.completed_jobs_count || 0, icon: Briefcase },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="card-base p-5">
                  {Icon && <Icon size={18} className="text-hive-ink" />}
                  <p className="mt-2 font-mono text-3xl font-bold text-worker">{s.value}</p>
                  <p className="text-xs uppercase tracking-widest text-smoke font-display">{s.label}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Portfolio — any plan; the backend truncates to portfolio_max_items */}
        {provider.portfolio_items?.length > 0 && (
          <section className="section bg-cream" data-testid="portfolio-section">
            <div className="container-app">
              <h2 className="font-display text-3xl font-bold text-worker">{t("providerLanding.portfolioTitle")}</h2>
              <p className="text-smoke mt-1">{t("providerLanding.portfolioSubtitle", { name: provider.company_name || provider.full_name })}</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {provider.portfolio_items.map((it) => (
                  <div key={it.item_id} className="card-base overflow-hidden" data-testid={`public-portfolio-${it.item_id}`}>
                    <div className="aspect-video bg-sand overflow-hidden">
                      <img
                        src={mediaUrl(it.image_url)}
                        alt={it.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                    <div className="p-4">
                      <p className="font-display font-bold text-worker">{it.title}</p>
                      {it.description && (
                        <p className="text-sm text-smoke mt-1">{it.description}</p>
                      )}
                      {it.project_url && (
                        <a href={it.project_url} target="_blank" rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-hive-ink hover:text-worker">
                          {t("providerLanding.viewProject")} <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* External payment options — render/relay only. FixyDany does not
            process these payments; the provider is paid off-platform. */}
        {/* The handles (T12-M4b). Copy, or open the app — the payment happens
            there and FixyDany is not part of it. */}
        {provider.payment_methods?.length > 0 && (
          <section className="section" data-testid="provider-payment-handles">
            <div className="container-app">
              <h2 className="font-display text-3xl font-bold text-worker">{t("providerLanding.paymentsTitle")}</h2>
              <p className="text-smoke mt-1 max-w-2xl">{t("payouts.clientContext")}</p>
              <div className="mt-6 max-w-xl">
                <PaymentHandleList methods={provider.payment_methods} />
              </div>
              <p className="text-xs text-smoke mt-4">{t("providerLanding.paymentsDisclaimer")}</p>
            </div>
          </section>
        )}

        {/* The retired https links, kept for their QR — which is what makes
            this useful at the door, in person. Every one of these columns was
            empty in production when M4b shipped, so this renders for nobody
            today and costs nothing to leave standing. */}
        {provider.payment_links && Object.keys(provider.payment_links).length > 0 && (
          <section className="section" data-testid="provider-payments">
            <div className="container-app">
              <h2 className="font-display text-3xl font-bold text-worker">{t("providerLanding.paymentsTitle")}</h2>
              <p className="text-smoke mt-1 max-w-2xl">{t("providerLanding.paymentsSubtitle")}</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: "cashapp", label: "Cash App" },
                  { key: "zelle", label: "Zelle" },
                  { key: "square", label: "Square" },
                ]
                  .filter((b) => provider.payment_links[b.key])
                  .map((b) => {
                    const link = provider.payment_links[b.key];
                    return (
                      <div key={b.key} className="card-base p-5 flex flex-col items-center text-center" data-testid={`payment-${b.key}`}>
                        <p className="font-display font-bold text-worker">{b.label}</p>
                        <div className="mt-3 bg-cream p-3 rounded-md border border-sand">
                          <QRCodeSVG value={link} size={140} />
                        </div>
                        <a href={link} target="_blank" rel="noopener noreferrer"
                          className="fx-btn fx-btn--secondary text-sm mt-4" data-testid={`payment-open-${b.key}`}>
                          {t("providerLanding.paymentOpen", { brand: b.label })} <ExternalLink size={12} />
                        </a>
                      </div>
                    );
                  })}
              </div>
              <p className="text-xs text-smoke mt-4">{t("providerLanding.paymentsDisclaimer")}</p>
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="section bg-linen">
          <div className="container-app">
            <h2 className="font-display text-3xl font-bold text-worker">{t("providerLanding.reviewsTitle")}</h2>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {reviews.length === 0 ? (
                <p className="text-smoke col-span-2">{t("providerLanding.noReviews")}</p>
              ) : (
                reviews.slice(0, 6).map((r) => (
                  <div key={r.review_id} className="card-base p-5">
                    <div className="flex gap-3 items-center">
                      <div className="w-10 h-10 rounded-full bg-hive flex items-center justify-center font-bold text-worker">
                        {(r.client_snippet?.full_name || "?").charAt(0)}
                      </div>
                      <div>
                        <p className="font-display font-semibold text-worker">{r.client_snippet?.full_name || t("providerLanding.anonymous")}</p>
                        <p className="text-xs text-smoke font-mono">{formatDate(r.created_at)}</p>
                      </div>
                      <div className="ml-auto flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} size={14} className={n <= Math.round(r.rating) ? "text-hive-ink fill-hive" : "text-sand"} />
                        ))}
                      </div>
                    </div>
                    {r.review_text && <p className="mt-3 text-worker">{r.review_text}</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
